import { RefreshCw } from "lucide-react";
import type {
  AnalysisResponse,
  PositionResponse,
} from "@patent-studio/api-types";
import { Button } from "@/components/ui/button";
import { Panel } from "@/components/ui/Panel";
import { needsDecision } from "./needs-decision";

/** "1 position was", "2 positions were": the count with the verb that agrees. */
function positionsWere(count: number): string {
  return count === 1 ? "1 position was" : `${count} positions were`;
}

/** What the bar says and whether it offers the re-run, from the one state that answers first. */
function stateOf(
  analysis: AnalysisResponse,
  attorney: readonly PositionResponse[],
): { heading: string; line: string; rerun: boolean; current: boolean } {
  if (analysis.stale) {
    return {
      heading: "Results are out of date",
      line: "The library or the positions changed since the last run. Make the rest of your changes, then re-run once.",
      rerun: true,
      current: false,
    };
  }
  const heading = `Assessed against ${analysis.library.length} prior art`;
  const short = analysis.positions.filter(
    (p) => p.not_assessed_against.length > 0,
  ).length;
  if (short > 0) {
    return {
      heading,
      line: `${positionsWere(short)} not compared with every document.`,
      rerun: true,
      current: false,
    };
  }
  const byId = new Map(attorney.map((p) => [p.position_id, p]));
  const due = analysis.positions.filter((p) =>
    needsDecision(p, byId.get(p.position_id)),
  ).length;
  if (due > 0) {
    return {
      heading,
      line:
        due === 1
          ? "1 position needs a decision before it can be confirmed."
          : `${due} positions need a decision before they can be confirmed.`,
      rerun: false,
      current: true,
    };
  }
  return {
    heading,
    line: "Every position has a verdict. Confirm the ones you want claim strategy to build on.",
    rerun: false,
    current: true,
  };
}

/**
 * The run bar at the top of the analysis: whether the results are current,
 * what they were assessed against, and what is left to decide. It never says
 * every position has a verdict while one was not compared with every
 * document, and offers one re-run when the results are out of date or
 * incomplete. Not drawn while a run is in motion, which says so itself.
 */
export function RunBar({
  analysis,
  attorney,
  running,
  refusal,
  onRerun,
}: {
  analysis: AnalysisResponse;
  attorney: readonly PositionResponse[];
  running: boolean;
  /** Why the server refused the last re-run, in its own words, or nothing. */
  refusal: string | null;
  onRerun: () => void;
}) {
  if (running) return null;
  const { heading, line, rerun, current } = stateOf(analysis, attorney);
  return (
    <Panel
      data-testid="run-bar"
      role="status"
      pad="snug"
      tone={current ? "success" : "warning"}
      className="mt-6 flex flex-wrap items-center gap-3 text-detail"
    >
      <span className="flex-1">
        <strong>{heading}</strong> {line}
      </span>
      {rerun ? (
        <Button variant="primary" size="dense" type="button" onClick={onRerun}>
          <RefreshCw aria-hidden />
          Re-run analysis
        </Button>
      ) : (
        <Button variant="secondary" size="dense" type="button" disabled>
          Analysis current
        </Button>
      )}
      {refusal !== null && (
        <p role="alert" className="basis-full text-destructive">
          It could not be re-run: {refusal}
        </p>
      )}
    </Panel>
  );
}
